import React from 'react';
import { Users, Target, Award, TrendingUp, CheckCircle, Heart } from 'lucide-react';

export const About: React.FC = () => {
  const team = [
    {
      name: "Fadi Dawood",
      role: "Founder & Lead Designer",
      image: "/Fadi Dawood.jpg",
      bio: "With over 20 years of experience in web design and development, Fadi brings unparalleled creative vision and technical expertise to every project. His passion for outstanding design and client success drives the company's mission."
    },
    {
      name: "Umar Munir",
      role: "Senior Developer",
      image: "/Umar Munir.webp",
      bio: "Umar specializes in modern web technologies and full-stack development. His expertise in React, Node.js, and database architecture ensures every website is built with scalability and performance in mind."
    },
    {
      name: "Shay Hafsa",
      role: "Senior Designer",
      image: "/Shay Hafsa.webp",
      bio: "Shay brings a keen eye for visual design and user experience. Her expertise in creating intuitive, beautiful interfaces helps transform client visions into compelling digital experiences that engage and convert."
    }
  ];

  const values = [
    {
      icon: <Heart className="h-8 w-8 text-primary-600" />,
      title: "Client-Focused",
      description: "Your success is our success. We prioritize your needs and goals in everything we do."
    },
    {
      icon: <Award className="h-8 w-8 text-primary-600" />,
      title: "Quality First",
      description: "We never compromise on quality. Every project receives our full attention and expertise."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-primary-600" />,
      title: "Innovation",
      description: "We stay ahead of trends and use cutting-edge technologies to deliver modern solutions."
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-primary-600" />,
      title: "Reliability",
      description: "Count on us for consistent communication, timely delivery, and ongoing support."
    }
  ];

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section className="pt-32 pb-32 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              About OutstandingNet
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              We're passionate about creating exceptional web experiences that help businesses thrive in the digital world.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in 2019, OutstandingNet began with a simple mission: to help businesses 
                  succeed online through exceptional web design and development. What started as a 
                  small team of passionate developers has grown into a trusted partner for companies 
                  of all sizes.
                </p>
                <p>
                  We believe that every business deserves a website that not only looks great but 
                  also performs exceptionally. Our approach combines creative design with technical 
                  excellence to deliver solutions that drive real results.
                </p>
                <p>
                  Today, we've helped over 500 businesses transform their online presence, and we're 
                  just getting started. Every project we take on is an opportunity to push boundaries 
                  and create something truly outstanding.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Team working together"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-accent-500 text-white p-6 rounded-lg">
                <div className="text-3xl font-bold">500+</div>
                <div className="text-sm">Projects Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Target className="h-8 w-8 text-primary-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                To empower businesses with outstanding web solutions that drive growth, 
                enhance user experience, and deliver measurable results. We're committed 
                to being the trusted partner that helps our clients succeed in the digital landscape.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <TrendingUp className="h-8 w-8 text-primary-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                To be the leading web design and development agency known for innovation, 
                quality, and exceptional client relationships. We envision a future where 
                every business has the tools they need to thrive online.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and shape how we work with our clients and each other.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="mb-6 flex justify-center transform group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our talented team of designers, developers, and strategists work together to bring your vision to life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="card p-8 text-center group">
                <div className="mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                  <h3 className="text-xl font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-primary-600 font-medium">{member.role}</p>
                </div>
                <p className="text-gray-600 leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Work with Us?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Let's discuss your project and see how we can help you achieve your goals.
          </p>
          <a href="/get-started" className="btn-secondary text-lg px-8 py-4">
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
};